import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { Media } from "@domain/media/media";
import { Organization } from "@domain/organization/organization";
import { useAsync, useIsMobile } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { Description } from "@ui/components/containers/description";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { AdvencedRichInput } from "@ui/components/inputs/draft/advencedRichInput";
import { Row, Stack, row, stack } from "@ui/components/layout";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { VideoPlayer } from "@ui/components/media/videoPlayer";
import { desktop, mobile } from "@ui/styles/utils";
import { ShareroomBlocIcons } from "@ui/pages/group/shareroom/shareroomInfo";
import { VideoPresentationEditor } from "@ui/pages/group/shareroom/videoPresentationEditor";
import { OrganizationDetails } from "./organizationDetails";

interface OrganizationPresentationProps extends React.HTMLAttributes<HTMLDivElement> {
	organization: Organization;
}
export const OrganizationPresentation: React.FC<OrganizationPresentationProps> = ({ organization, ...htmlProps }) => {
	const [fullSize, setFullSize] = useState(false);
	const [height, setHeight] = useState(0);
	const [contentHeight, setContentHeight] = useState(0);
	const isMobile = useIsMobile();
	const alias = useOrganizationAlias();
	// const { createModal } = useModalTools();

	const detailsRef = useRef<HTMLDivElement>(null);
	const presentationRef = useRef<HTMLDivElement>(null);

	const updateHeight = (): void => {
		setHeight(isMobile ? 300 : (detailsRef.current?.getBoundingClientRect().height ?? height));
		setFullSize(false);
	};

	useEffect(() => {
		setTimeout(() => {
			setContentHeight(presentationRef.current?.scrollHeight ?? contentHeight);
			setFullSize(false);
		}, 100); // Fixes draft JS Renderer having late dom updates
	}, [organization.presentation]);

	const isHidden = !fullSize && contentHeight > height;

	useEffect(updateHeight, [isMobile, organization.categories.length, alias]);

	// const editPresentation = (): void =>
	// 	createModal((close) => <PresentationEditorModal close={close} organization={organization} />);

	// const isMobile = useIsMobile();

	const [editMode, setEditMode] = useState(false);
	const [presentation, setPresentation] = useState(organization.presentation ?? ContentState.createFromText(""));
	const [presentationAsset, setPresentationAsset] = useState<{ url?: string; video?: Media }>(
		organization.presentationAsset ?? { url: undefined, video: undefined }
	);

	const { organizationService } = useServices();

	const { run, loading } = useAsync(async () => {
		await organizationService.savePresentation(organization, presentation, presentationAsset);
	}, [organization, presentation, presentationAsset]);

	return (
		<Container {...htmlProps}>
			{/* <PresentationView
				ref={presentationRef}
				isHidden={isHidden}
				defaultHeight={height}
				fullSize={fullSize}
				onClick={() => isHidden && setFullSize(true)}
			>
				<Row gap={10} justify="space-between">
					<Subtitle>
						<div>{I18n.t("organization.presentation.title")}</div>
						{alias && <EditIcon onClick={editPresentation} />}
					</Subtitle>
					{isHidden && <FoldButton>{I18n.t("see_more")}</FoldButton>}
					{fullSize && <FoldButton onClick={() => setFullSize(false)}>{I18n.t("see_less")}</FoldButton>}
				</Row>
				<div>
					{organization.presentation?.getPlainText().trim() ? (
						<DraftRenderer content={organization.presentation} />
					) : (
						<>
							<Secondary>{I18n.t("organization.presentation.empty")}</Secondary>
							{!!alias && (
								<AddPresentationButton onClick={editPresentation}>
									{I18n.t("organization.presentation.button")}
								</AddPresentationButton>
							)}
						</>
					)}
				</div>
			</PresentationView>
			<DetailsWrapper ref={detailsRef}>
				<OrganizationDetails organization={organization} onToggle={updateHeight} />
			</DetailsWrapper> */}
			{/* <Container {...htmlProps}> */}
			<PresentationView
			// ref={presentationRef}
			// isHidden={isHidden}
			// defaultHeight={height}
			// fullSize={fullSize}
			// onClick={() => isHidden && setFullSize(true)}
			>
				<Row align="center" justify="space-between" style={{ width: "100%" }}>
					<StyledPrimarybold>{I18n.t("organization.presentation.title")}</StyledPrimarybold>
					{alias && !editMode ? (
						<RoundedPlusButton
							onClick={() => {
								setEditMode(!editMode);
								if (isHidden) setFullSize(true);
							}}
							label={!presentation?.hasText() ? I18n.t("add") : I18n.t("edit")}
						/>
					) : null}
				</Row>
				{alias && editMode ? (
					<Stack align="center" gap={100} style={{ width: "100%" }}>
						<VideoPresentationEditor
							presentationAsset={presentationAsset}
							onChange={(value) => {
								setPresentationAsset(value);
							}}
						/>

						<RichText
							// key={field}
							value={presentation}
							onChange={(value) => setPresentation(value)}
						/>
						{/* {loading ? (
						<ClipLoader />
					) : ( */}
						<Row gap={30}>
							<SecondaryButton onClick={() => setEditMode(false)}>{I18n.t("cancel")}</SecondaryButton>
							<LoadingButton
								loading={loading}
								onClick={() => {
									setEditMode(false);
									run();
								}}
							>
								{I18n.t("save")}
							</LoadingButton>
						</Row>
						{/* )} */}
					</Stack>
				) : !presentation?.hasText() ? (
					<Stack gap={100} align="center" justify="center" style={{ width: "100%" }}>
						{organization.presentationAsset?.url ? (
							<StyledVideoPlayer space={isMobile ? 146.25 : 30.25} source={organization.presentationAsset?.url} />
						) : organization.presentationAsset?.video ? (
							<StyledTooshareVideo asset={organization.presentationAsset?.video} />
						) : null}
						<IconView>
							<img src={ShareroomBlocIcons["presentation"]} />
						</IconView>

						<Description>
							{I18n.t(`organization.about.${"presentation"}.${alias ? "owner" : "member"}.description`)}
						</Description>
					</Stack>
				) : presentation !== undefined ? (
					<Stack gap={100} style={{ width: "100%" }}>
						{organization.presentationAsset?.url ? (
							<StyledVideoPlayer space={isMobile ? 146.25 : 30.25} source={organization.presentationAsset?.url} />
						) : organization.presentationAsset?.video ? (
							<StyledTooshareVideo asset={organization.presentationAsset?.video} />
						) : null}
						<DraftRenderer content={presentation} />
					</Stack>
				) : null}
			</PresentationView>
			<DetailsWrapper ref={detailsRef}>
				<OrganizationDetails organization={organization} onToggle={updateHeight} />
			</DetailsWrapper>
		</Container>
	);
};

// const Container = styled.div`
// 	@media ${desktop} {
// 		${row(40)}
// 	}
// 	@media ${mobile} {
// 		${stack(20, "flex-start", "stretch", "reverse")};
// 	}
// `;

const Container = styled.div`
	display: grid;
	grid-template-areas: "pr  dt";
	grid-template-columns: 1fr 280px;
	grid-template-rows: auto auto;
	grid-column-gap: 20px;
	@media ${mobile} {
		${stack(20, "flex-start", "stretch", "reverse")};
	}
`;

// const Subtitle = styled.div`
// 	${row(6, "flex-start", "center")};
// 	> div {
// 		${({ theme }) => theme.textStyles.subtitle};
// 	}
// 	> svg {
// 		display: block;
// 		cursor: pointer;

// 		path {
// 			fill: ${({ theme }) => theme.colors.secondary};
// 		}
// 	}
// `;

// const FoldButton = styled(PrimaryMedium)`
// 	cursor: pointer;
// 	color: ${({ theme }) => theme.colors.secondary};
// `;

const PresentationView = styled(RoundedBlock)<{ defaultHeight?: number; isHidden?: boolean; fullSize?: boolean }>`
	grid-area: pr;
	padding: 20px;
	position: relative;
	${stack(30)}
`;

// const PresentationView = styled(RoundedBlock)<{ defaultHeight?: number; isHidden?: boolean; fullSize?: boolean }>`
// 	flex: 1;
// 	padding: 20px;
// 	position: relative;
// 	${gap(20)};
// 	@media ${desktop} {
// 		min-height: ${({ defaultHeight }) => defaultHeight}px;
// 	}
// 	${({ defaultHeight, fullSize }) => !fullSize && `max-height: ${defaultHeight}px`};
// 	${({ isHidden }) =>
// 		isHidden &&
// 		css`
// 			overflow: hidden;
// 			cursor: pointer;
// 			&::after {
// 				z-index: ${Z.over};
// 				position: absolute;
// 				left: 0;
// 				right: 0;
// 				top: 70%;
// 				bottom: 0;
// 				content: "";
// 				background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 1) 100%);
// 			}
// 		`};
// `;

const DetailsWrapper = styled.div`
	grid-area: dt;
`;

// const AddPresentationButton = styled(PrimaryButton)`
// 	margin: 100px auto;
// 	@media ${mobile} {
// 		margin: 20px auto;
// 	}
// `;

// const Container = styled(RoundedBlock)`
// 	padding: 30px 40px;
// 	${stack(30)}
// 	width:100%;
// 	@media ${mobile} {
// 		padding: 20px;
// 	}
// `;
// const Container = styled.div`
// 	@media ${desktop} {
// 		${row(40)}
// 	}
// 	@media ${mobile} {
// 		${stack(20, "flex-start", "stretch", "reverse")};
// 	}
// `;

const RichText = styled(AdvencedRichInput)<{ isFocused?: boolean; isInvalid?: boolean }>`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	border-color: ${({ theme, isFocused, isInvalid }) =>
		isInvalid ? theme.colors.red : isFocused ? theme.colors.secondary : theme.colors.lightergray};
	cursor: text;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 15px;
		font-size: 0.9375rem;
	}
	width: 100%;
	.DraftEditor-editorContainer {
		min-height: 200px;
	}
`;

const StyledPrimarybold = styled(PrimaryBold)`
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 1.5rem;
	font-weight: 800;

	@media ${mobile} {
		font-size: 0.813rem;
	}
`;

const IconView = styled.div`
	${row(0, "center", "center")}
	width: 100%;
	height: 100%;
	> img {
		@media ${desktop} {
			width: 347px;
			height: 217px;
		}
	}
`;

const StyledTooshareVideo = styled(TooshareVideoPlayer)`
	width: 100%;
	height: 328px;
	margin-bottom: 60px !important;
	.quality-control {
		display: none;
	}
`;

const StyledVideoPlayer = styled(VideoPlayer)`
	> iframe {
		height: 328px;
	}
	> div {
		padding-top: 0 !important;
	}
`;
